import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { NoBackend } from "@/components/common/NoBackend";
import { publicClient } from "@/graphql/public-client";
import { ListingState } from "@/graphql/generated/operations";
import { PublicOpenListingsDocument } from "@/graphql/generated/public";
import { userErrorMessage } from "@/lib/user-error";
import { ListingCard, type ListingTile } from "@/pages/share/listings/ListingCard";

// SHARE: Offene Ausschreibungen ohne Konto: dieselben Karten wie mit Konto, gelesen nur aus dem öffentlichen Schema,
// das allein bestimmt, was öffentlich ist (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item).
// Ohne Suche nach Kennziffer: das öffentliche Schema bietet absichtlich keine Suche an.
export function PublicOpenListingsPage() {
  const intl = useIntl();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const { data, loading, error, refetch } = useQuery(PublicOpenListingsDocument, {
    client: publicClient,
    variables: { after: cursors.at(-1) },
  });
  const page = data?.publicListings;
  const tiles: ListingTile[] = (page?.nodes ?? []).map((listing) => ({
    __typename: "Listing",
    id: listing.id,
    reference: listing.reference,
    name: listing.name,
    description: listing.description,
    deadline: listing.deadline,
    publishedOn: listing.publishedOn,
    // The public set is open by definition: genesis serves no other listing here.
    state: ListingState.Open,
    mainImage: listing.mainImage ? { __typename: "File", id: listing.id, ...listing.mainImage } : null,
  }));

  return (
    <main id="public-open-listings" className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="PublicOpenListings">
      <h1 className="smycles-page-title">
        <FormattedMessage id="share.openListings.title" defaultMessage="Offene Ausschreibungen" />
      </h1>
      <p className="mt-4 max-w-md text-smycles-body text-ink">
        <FormattedMessage
          id="share.public.openListings.subtitle"
          defaultMessage="Stellen, Programme und Aufrufe, auf die du dich auch ohne Konto bewerben kannst."
        />
      </p>
      <div className="mt-5">
        {loading && !page ? (
          <div className="text-smycles-label text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Laden…" />
          </div>
        ) : error ? (
          <div role="alert">
            <p className="text-smycles-label text-ink-muted">{userErrorMessage(intl, error)}</p>
            <Button id="public-open-listings-retry" variant="secondary" className="mt-3" onClick={() => void refetch()}>
              <FormattedMessage id="share.public.retry" defaultMessage="Erneut versuchen" />
            </Button>
          </div>
        ) : tiles.length === 0 ? (
          <NoBackend label={intl.formatMessage({ id: "share.openListings.empty", defaultMessage: "Gerade ist keine Ausschreibung offen" })} />
        ) : (
          <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
            {tiles.map((listing, index) => (
              <ListingCard variant="feed" key={listing.id} listing={listing} index={index} to={`/share/open-listings/${listing.id}`} />
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-between gap-4">
          {cursors.length > 1 ? (
            <Button id="public-open-listings-prev" variant="secondary" disabled={loading}
              onClick={() => setCursors((value) => value.slice(0, -1))}>
              <FormattedMessage id="common.prevPage" defaultMessage="Zurück" />
            </Button>
          ) : <span />}
          {!error && page?.pageInfo.hasNextPage && page.pageInfo.endCursor ? (
            <Button id="public-open-listings-next" variant="secondary" disabled={loading}
              onClick={() => setCursors((value) => [...value, page.pageInfo.endCursor ?? null])}>
              <FormattedMessage id="common.nextPage" defaultMessage="Weiter" />
            </Button>
          ) : null}
        </div>
      </div>
    </main>
  );
}
